import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import CruiseShipFacts from './CruiseShipFacts'

const OverviewDetails = styled(Group)`
  flex: 1;
  min-width: 0;
`

interface Props {
  offer: App.CruiseOffer
  showImages?: boolean
}

function CruiseShipOverview({ offer, showImages = true }: Props) {
  const intl = useIntl()
  const shipImages = offer.ship.images || []

  const ageRestrictions = useMemo(() => {
    const { adultMinimumAge } = offer.ship.occupancies || {}
    const firstDeparture = Object.values(offer.departures).flat()[0]
    const minimumSupervisorAge = firstDeparture?.minimumSupervisorAge
    if (minimumSupervisorAge && adultMinimumAge) {
      return intl.formatMessage(
        {
          id: '0ASndW3gxYgGcmrYzB_x',
          defaultMessage: 'Age restrictions: passengers under {adultMinimumAge} must travel with a guardian aged {minimumSupervisorAge} or older.',
          description: 'Age restriction message for cruise ship in the cruise offer or booking section',
        },
        { adultMinimumAge, minimumSupervisorAge },
      )
    }
  }, [offer.ship.occupancies, offer.departures, intl])

  return <Group direction="horizontal" horizontalAlign="stretch" gap={48}>
    <OverviewDetails direction="vertical" gap={20}>
      {showImages && <CSSBreakpoint max="tablet">
        <ImageGallery
          images={shipImages}
          showImageTitles={false}
        />
      </CSSBreakpoint>}

      <Group direction="vertical" gap={8}>
        <Heading as="h4" variant="heading3">
          {offer.ship.name}
        </Heading>
        <BodyText variant="medium"><span>{offer.cruiseLine.name}</span></BodyText>
      </Group>

      <Group direction="vertical" gap={16} horizontalAlign="start">
        <CruiseShipFacts offer={offer} />
      </Group>

      {ageRestrictions && <MessageBanner icon={<LineInfoCircleIcon />} kind="informational">
        {ageRestrictions}
      </MessageBanner>}
    </OverviewDetails>

    {showImages && <CSSBreakpoint min="desktop">
      <ImageGallery
        images={shipImages}
        showImageTitles={false}
      />
    </CSSBreakpoint>}
  </Group>
}

export default CruiseShipOverview
